// // ====================================
// //
// //   SCORM.js
// //
// // ====================================

import pipwerks from './SCORM_API_wrapper';
import setValue from 'set-value';
import { merge, debounce } from 'lodash-es';
import getURLParameter from './getQueryParameter';
import removeNull from './removeNull';
import { nextTick } from 'vue';
import ScrollTrigger from 'gsap/ScrollTrigger';
import pako from 'pako';
import type { SubmissionData } from '../../types/InsightsData';

// import dotProp from 'dot-prop';

// const { V } = window;

function compressString(input) {
  const utf8Encoder = new TextEncoder();
  const inputBytes = utf8Encoder.encode(input);
  const compressedBytes = pako.deflate(inputBytes);
  const compressedString = btoa(String.fromCharCode(...compressedBytes));

  return compressedString;
}

function decompressString(compressed) {
  const compressedBytes = Uint8Array.from(atob(compressed), c =>
    c.charCodeAt(0)
  );
  const decompressedBytes = pako.inflate(compressedBytes);
  const utf8Decoder = new TextDecoder();
  return utf8Decoder.decode(decompressedBytes);
}

function encodeSuspendData(suspendString) {
  return encodeURIComponent(compressString(suspendString));
}

function decodeSuspendData(suspendString) {
  const defaultString = '{}';

  // if the suspendString is empty, return an empty object
  if (!suspendString || suspendString.length < 1) {
    return defaultString;
  }

  // try to URI decode and decompress the suspend_data string
  try {
    const decodedDecompressed = decompressString(
      decodeURIComponent(suspendString)
    );
    const decodedDecompressedMatch = decodedDecompressed.match(/json=([^;]+);/);

    if (decodedDecompressedMatch) {
      return decodedDecompressedMatch[1];
    } else {
      throw new Error(
        'Invalid suspend data format. Expected "json=..." pattern.'
      );
    }
  } catch (e) {
    console.error('Error decoding suspend data. Resetting.', e.message);
    return defaultString;
  }
}

// shortcut for debugging. Pass the 'encoded' part of the sd string
// zipped=encoded;
// window.decodeSD('encoded')
window.decodeSD = sd => JSON.parse(decodeSuspendData(sd));

// reduce the suspend_data string by de-duplicating IDs from viewed once we complete them
function removeViewedWhenCompleted() {
  const viewed = get('viewed') || {};
  const completed = get('completed') || {};
  const viewedIds = Object.keys(viewed);
  const completedIds = Object.keys(completed);
  const viewNotCompleted = viewedIds.filter(id => !completedIds.includes(id));
  const onlyViewed = viewNotCompleted.reduce((acc, id) => {
    acc[id] = 1;
    return acc;
  }, {});

  set('viewed', onlyViewed);
}

const pw = pipwerks.SCORM;
window.pipwerks = pipwerks;

const pwSet = (k, v) => {
  // cant trust the course player
  try {
    pipwerks.SCORM.set(k, v);
    pipwerks.SCORM.save();
  } catch (e) {
    console.error(e);
  }
};

const quit = () => {
  try {
    pw.quit();
  } catch (e) {
    console.error(e);
  }
};

const SCORM = {
  // any variable in suspend data
  get,
  set,

  // set tags in Vue
  setTags,

  // page views
  setViewed,
  getViewed,

  // completed activities
  setCompleted,
  getCompleted,

  // cmi.core.lesson_location
  setLocation,
  getLocation,

  // cmi.core.score.raw
  setScore,
  getScore,
  // addToScore,

  getPassmark,

  // cmi.core.lesson_status
  passed,
  failed,
  getLessonStatus,
  setLessonStatus,

  // get Var_firm_name
  getFirm,
  isFirm,

  // get Var_courseid
  getCourseId,

  // console.log url representing saved session
  log,

  // is there an active SCORM connection?
  isConnected,

  // SCORM session
  quit,

  fullName: 'Demo User',
  firstName: 'Demo',
  lastName: 'User',
  userName: 'demo@example.com'
};

// initialize connection, take 3 tries
pw.version = '1.2';
let initSuccess;
let initAttempts = 3;

while (initAttempts--) {
  try {
    initSuccess = pw.init();
  } catch (e) {
    // null
  }

  if (initSuccess) {
    initAttempts = 0;
  }
}

export function isConnected() {
  return pw.connection.isActive;
}

// name variables
if (isConnected()) {
  SCORM.fullName = pw.get('cmi.core.student_name');
  [SCORM.lastName, SCORM.firstName] = SCORM.fullName.split(',');
  SCORM.userName = pw.get('cmi.core.student_id');
}

const isDemo =
  SCORM.userName === 'MASTERDEMO' || SCORM.userName === 'MASTERANONYMOUS';

if (isDemo) {
  pw.set = () => false;
}

let suspendString = '';

// object for accessing suspend data variables during runtime
let suspendObject = {};

// store key-value pairs generated by LMS separately
const suspendPairs = {};

// initialize `suspendObject` and `suspendPairs`
initSuspendObject();

let score = parseInt(
  isConnected()
    ? pw.get('cmi.core.score.raw') || 0
    : getURLParameter('score') || 0
);

export function setScore(n: number) {
  if (Number.isNaN(n)) return;
  score = n;
  pwSet('cmi.core.score.raw', score);
  logCMI('cmi.core.score.raw', 'score', score, score);
}

export function getScore() {
  return score;
}

// if the score variable is falsy, initialize to 0
if (!score && score !== 0) {
  setScore(0);
}

// add an id to `viewedPages`
export function setViewed(id: string) {
  if (id) {
    set(`viewed.${id}`, 1);
  }
}

// return whether id exists in `viewedPages`
export function getViewed(id: string) {
  const viewed = get('viewed') || {};
  return Boolean(viewed[id]);
}

// add an id to `completed`
export function setCompleted(id: string) {
  // uc-assessment provides a dummy sectionID '___' for the assessment screen (don't store this)
  if (id && id !== '___') {
    set(`completed.${id}`, 1);
  }
}

// return whether id exists in `completed`
export function getCompleted(id: string) {
  const completed = get('completed') || {};
  return Boolean(completed[id]);
}

/**
 * Sets the current state of audio
 */

export function setAudioState(playing: boolean) {
  // don't set if the value is the same as the current state
  if (playing === Boolean(get('audioplaying'))) {
    return;
  }

  set('audioplaying', playing);
}

// get location from LMS or from URL parameter
let lessonLocation = isConnected()
  ? pw.get('cmi.core.lesson_location')
  : getURLParameter('p');

if (isDemo) {
  lessonLocation = false;
}

export function setLocation(name: string) {
  lessonLocation = name;
  window.app.location = name;
  pwSet('cmi.core.lesson_location', lessonLocation);

  logCMI(
    'cmi.core.lesson_location',
    lessonLocation,
    lessonLocation,
    lessonLocation
  );
}

export function getLocation() {
  return lessonLocation;
}

// get cmi.core.lesson_status
let lessonStatus = isConnected()
  ? pw.get('cmi.core.lesson_status')
  : (getURLParameter('status') ?? suspendObject?.passed)
    ? 'passed'
    : suspendObject?.failed
      ? 'failed'
      : 'incomplete';

export function setLessonStatus(status: string) {
  lessonStatus = status;
  pwSet('cmi.core.lesson_status', lessonStatus);
}

export function getLessonStatus() {
  return lessonStatus;
}

// pass the course
export function passed() {
  console.log('PASSED');
  setLessonStatus('passed');
  window.app.passed = true;

  // trigger content refresh
  setTags();
}

// fail the course
export function failed() {
  console.log('FAILED');
  setLessonStatus('failed');
  window.app.passed = false;
}

export function getFirm() {
  return (
    window.SCORM_firm ||
    getURLParameter('firm') ||
    get('Var_firm_name') ||
    getURLParameter('tenant') ||
    '___'
  );
}

/**
 * Returns the course ID defined in suspend data as an integer.
 *
 * (The ID may also be configured by the URL parameter &courseid=999, but suspend data in SCORM has priority.)
 *
 * If the ID is not a valid number, returns null. 0 is valid.
 *
 */
export function getCourseId(): number {
  const cid =
    (window.SCORM_courseId as number) ??
    Number(getURLParameter('courseid') ?? get('Var_courseid'));

  if (!(Number.isInteger(cid) && cid >= 0)) {
    console.error(
      `No valid course ID found. \n\nPlease check for an ID in SCORM's suspend data or the URL's query string.`
    );
    return false;
    throw new Error(
      `No valid course ID found. \n\nPlease check for an ID in SCORM's suspend data or the URL's query string.`
    );
  }

  return cid;
}

export function getPassmark() {
  const cid = Number(getURLParameter('passmark') ?? get('Varpassmark'));

  return cid >= 0 ? cid : 50;
}

/**
 * Merges an object of tab settings into the existing tab settings.
 *
 * Returns a promise that resolves after the next Vue tick and dispatching the `tagschanged` event.
 *
 * Example:
 *
 * ```js
 * setTags({ a: 1, b: 1, c: 1 })
 * setTags({ c: 0, d: 1 })
 *
 * V.SCORM.get('tags')  // { a: 1, b: 1, c: 0, d: 1 }
 * ```
 *
 * @param {Tags} tags
 */
export function setTags(tags: Tags = {}) {
  // get a list of pages ready in case current page is turned off
  // const currentPageId = getLocation();
  // const allPageIds = [...document.querySelectorAll('.page')].map(p => p.id);
  // const currentPageIndex = allPageIds.findIndex(id => id === currentPageId);

  // value is 1 or 0
  Object.keys(tags).forEach(k => {
    tags[k] = tags[k] ? 1 : 0;
  });

  const current = get('tags') || {};
  set('tags', { ...current, ...tags });

  nextTick(() => {
    ScrollTrigger.refresh();
    window.dispatchEvent(new CustomEvent('tagschanged'));

    // FIXME
    // if the current page is gone, go to the closest page before it, if there is one, otherwise the closest page after it
    // if (!document.getElementById(currentPageId)) {
    //   const availablePages = allPageIds.map(id => document.getElementById(id));

    //   let targetPage;
    //   let i = currentPageIndex;

    //   while (i-- > 0 && !targetPage) {
    //     if (availablePages[i]) {
    //       targetPage = availablePages[i];
    //     }
    //   }

    //   i = currentPageIndex;

    //   while (++i < allPageIds.length && !targetPage) {
    //     if (availablePages[i]) {
    //       targetPage = availablePages[i].id;
    //     }
    //   }

    //   V.Navigation.go(targetPage);
    // }
  });
}

// window.whichTags = () => {
//   const output = [];
//   const t = get('tags');

//   Object.keys(t).forEach(k => {
//     if (t[k]) {
//       output.push(k);
//     }
//   });

//   return output;
// };

export function isFirm(firm: string) {
  return firm.toUpperCase() === getFirm().toUpperCase();
}

// set cmi.core.exit before unload
window.addEventListener('beforeunload', setExit);
window.addEventListener('unload', setExit);

export function setExit() {
  const exit = lessonStatus === 'incomplete' ? 'suspend' : 'logout';
  console.log('setExit cmi.core.exit: ', exit, typeof exit);

  pwSet('cmi.core.exit', exit);
  pw.quit();
}

export function setSessionTime() {
  pw.setSessionTime();
}

export function sendInsightsData(insightsData: SubmissionData): boolean {
  const success = pw.sendInsightsData(insightsData);
  if (success) {
    console.log('Insights data sent successfully');
    return true;
  } else {
    console.log('Failed to send insights data');
    return false;
  }
}

// return a query string representing an offline session
export function log() {
  const sd = saveSuspendData();

  return (
    window.location.origin +
    window.location.pathname +
    `?firm=${getFirm()}&courseid=${getCourseId()}&p=${lessonLocation}&sd=${sd}&score=${score}`
  );
}

/**
 *
 */
export function initSuspendObject() {
  // get the string from LMS or URL parameter
  if (isConnected()) {
    suspendString = pw.get('cmi.suspend_data');
  } else if (getURLParameter('sd')) {
    suspendString = getURLParameter('sd');
  }

  console.log(`Suspend Data:`, suspendString);

  const uncompressedMatch = suspendString.match(/json=([^;]+);/);
  const compressedMatch = suspendString.match(/zipped=([^;]+);/);
  const pairs = suspendString.match(/([^=;]+)=([^=;]+);/g) || [];

  let JSONString = '{}';
  if (uncompressedMatch) {
    JSONString = uncompressedMatch[1];

    // remove invalid line break char
    JSONString = JSONString.replace(/%0A/g, ''); // line breaks

    // decode characters we encode when saving
    JSONString = JSONString.replace(/%3A/g, ':');
    JSONString = JSONString.replace(/%3B/g, ';');
    JSONString = JSONString.replace(/%3D/g, '=');
  } else if (compressedMatch) {
    // new compressed data format with 'zipped=' prefix
    JSONString = decodeSuspendData(compressedMatch[1]);
  } else {
    // old compressed data format from local-sco branch (no prefix, no extra key=value; pairs in the string)
    if (pairs.length === 0) {
      JSONString = decodeSuspendData(suspendString);
    }
  }

  // parse the JSON and store in suspendObject
  suspendObject = JSON.parse(JSONString);

  const urlTagsParameter = getURLParameter('tags');
  const urlTagsObject = {};

  if (!suspendObject.tags && urlTagsParameter) {
    urlTagsParameter.split(/[+ ]+/).forEach(t => {
      urlTagsObject[t] = 'url';
    });

    suspendObject.tags = urlTagsObject;
  }

  // parse the rest of the string
  let i = pairs.length;

  // add key=value; pairs to suspendPairs
  while (i--) {
    // remove final ';' and split on '='
    const [key, value] = pairs[i].slice(0, -1).split('=');

    // ignore json and zipped keys
    if (key !== 'json' && key !== 'zipped') {
      suspendPairs[key] = value;
    }
  }

  if (isDemo) {
    suspendObject = {};
  }

  window.sd = suspendObject;

  return suspendObject;
}

/**
 * Stringifies the variables object and saves it to
 * the LMS or offlineSuspendData.
 */
export function saveSuspendData() {
  // // timer for testing purposes
  // var start = performance.now();

  // remove null values from suspendObject
  const cleanedSuspendObject = removeNull(suspendObject);
  const JSONString = JSON.stringify(cleanedSuspendObject);
  const encodedString = `zipped=${encodeSuspendData(`json=${JSONString};`)};`;

  // convert object to string
  let pairsString = '';

  Object.keys(suspendPairs).forEach(key => {
    pairsString += `${key}=${suspendPairs[key]};`;
  });

  const sdString = encodedString + pairsString;

  // Update the LMS with the new suspend data string
  if (isConnected()) {
    pwSet('cmi.suspend_data', sdString);
  }

  suspendString = sdString;
  SCORM.offlineSuspendData = sdString;

  reportSize(sdString);

  // // log timer result
  // console.log(
  //   'saveSuspendData() took ',
  //   Math.round(performance.now()*1e3 - start*1e3)/1e3,
  //   'ms'
  // );

  // console.log('saveSuspendData()');

  return sdString;
}

const reportSize = (suspendString: string) => {
  const MAX_SIZE = pw.version === '1.2' ? 4096 : 65536;
  const MAX_SIZE_TEXT = pw.version === '1.2' ? '4KB' : '64KB';

  // output the data size in KB of the compressed string
  const compressedSize = suspendString.length / 1024;
  console.log(
    `Compressed suspend_data size: ${compressedSize.toFixed(2)}KB (${
      suspendString.length
    } bytes). Limit: ${MAX_SIZE_TEXT}`
  );

  // warn if over 4KB for SCORM 1.2 or 64KB for SCORM 2004
  if (suspendString.length > MAX_SIZE) {
    console.warn(
      `Compressed suspend_data size (${compressedSize.toFixed(
        2
      )}KB) exceeds ${MAX_SIZE_TEXT} limit!`
    );
  }
};

/**
 * Debounces `saveSuspendData()` so that if `set()` is called multiple
 * times in the same function, all the changes are sent to the LMS in a
 * single update.
 */
export const debouncedSaveSuspendData = debounce(saveSuspendData, 250);

/**
 * Returns data from the variables object. The parameter `name`
 * is the name of the requested property as a string, and it may
 * be multiple levels deep, e.g. `name = 'level1.level2.level3'`.
 *
 * @param  {string} name - The name of the property to return.
 * @return {*} value - The property's value.
 */
export function get(name?: string): unknown {
  if (typeof name === 'undefined') {
    return suspendObject;
  }

  if (suspendPairs[name]) {
    return suspendPairs[name];
  }

  const levelNames = name.split('.');
  let value = suspendObject;

  // loop down to deepest level
  for (let i = 0, iMax = levelNames.length; i < iMax; i++) {
    if (levelNames[i] in value) {
      value = value[levelNames[i]];
    } else {
      // if at any time a property is not defined, return undefined
      return;
    }
  }

  return value;
}

function logCMI(cmi: string, name: string, value: any, updated: any) {
  console.groupCollapsed(
    `%cLMS  %c${cmi}  %c` + name,
    'color:gray;font-weight: lighter',
    'color:#070;font-weight: bold',
    'color:initial;font-weight: normal'
  );

  console.log({ [name]: value });
  console.log(updated);
  console.groupEnd();
}

/**
 * Sets a value in the variables object and saves to suspend data. The value may be nested in a property any number of levels deep, by passing the first parameter for example as `set('level1.level2.level3', 42)`. If intermediate levels do not exist, then they are created.
 *
 * If an object is the only argument, it is deep merged (with `lodash.merge`) into suspend data, for example `set({ a: { b: { c: 42 } } }).
 *
 */
export function set(obj: object, value?: never, options?: never): boolean;
export function set(name: string, value?: any): boolean;
export function set(name: string | object, value: any = null) {
  if (typeof name === 'object') {
    merge(window.app.suspend, name);
    merge(suspendObject, name);

    return true;
  } else if (suspendPairs[name]) {
    suspendPairs[name] = value;
  } else {
    setValue(suspendObject, name, value);
    setValue(window.app.suspend, name, value);
    // window.app.suspend = { ...window.app.suspend, [name]: value };
  }

  // console.log(`set(${name}, ${JSON.stringify(value)})`);
  logCMI('cmi.suspend_data', name, value, suspendObject);

  return true;
}

// alt+S shortcut in dev
if (import.meta.env.DEV) {
  window.addEventListener('keydown', function (e) {
    if (e.key === 's' && e.altKey) window.open(log());
  });
}

window.SCORM = SCORM;
window.decodeSuspendData = decodeSuspendData;
// window.app.suspend = suspendObject;

export default SCORM;
